import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { PreBill, PreBillState } from 'src/app/core/interfaces/preBill';
import { PaginationList } from 'src/app/core/models/pagination-list';
import { PreBillService } from 'src/app/core/services/pre-bill.service';
import { Utils } from 'src/app/core/resources/utils';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { PreBillFormComponent, PreBillFormMode } from '../pre-bill-form/pre-bill-form.component';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { Origin } from 'src/app/core/interfaces/origin';
import { Destination } from 'src/app/core/interfaces/destination';
import { PreBillPreview, PreBillPreviewColumn, PreBillPreviewRow } from 'src/app/core/interfaces/preBillPreview';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { CargoManager } from 'src/app/core/managers/cargo.manager';
import { ResponseList } from 'src/app/core/interfaces/response-list';
import {
  PreBillFingerprintHistory,
  PreBillFingerprintType
} from 'src/app/core/interfaces/preBillFingerprintHistory';
import { DialogComponent } from 'src/app/shared/dialog/dialog.component';
import { DateManager } from 'src/app/core/managers/date.manager';
import { CargoDetailService } from '../cargo-detail/cargo-detail.service';
import { CompanyNamePipe } from 'src/app/core/pipe/companyName.pipe';
import { AuthService } from 'src/app/core/services/authentication.service';
import { SettingId } from 'src/app/core/interfaces/saas-config';
import { ActivatedRoute, Router } from '@angular/router';

interface PreBillTab {
  state: PreBillState;
  stateLabel: string;
  label: string;
}

@Component({
  selector: 'app-pre-bill-list',
  templateUrl: './pre-bill-list.component.html',
  styleUrls: ['./pre-bill-list.component.scss'],
  providers: [CompanyNamePipe]
})
export class PreBillListComponent implements OnInit, OnDestroy {

  public readonly tabs: PreBillTab[] = [
    { state: PreBillState.Created, label: 'Creadas', stateLabel: 'Creada' },
    { state: PreBillState.Cashed, label: 'Facturadas', stateLabel: 'Facturada' },
    { state: PreBillState.Deleted, label: 'Eliminadas', stateLabel: 'Eliminada' }
  ];

  public activeState: PreBillState = this.tabs[0].state;
  public paginationList: PaginationList = new PaginationList();
  public listLoading: boolean = true;
  public consecutiveControl: FormControl = new FormControl(null);
  public companyControl: FormControl = new FormControl(null);
  public validatedControl: FormControl = new FormControl('all');
  private expandedIds: { [id: string]: boolean } = {};
  private loadingCargosIds: { [id: string]: boolean } = {};
  private loadingPreviewIds: { [id: string]: boolean } = {};
  private previews: { [id: string]: PreBillPreview } = {};
  private lastCompanyId: string = undefined;
  private subscriptions: Subscription[] = [];
  // Empresa de transporte de la pastilla; la URL (?transportCompanyId=) la conserva al recargar.
  public initialTransportCompanyId: string = null;
  public transportCompanyId: string = null;
  private transportCompanyResolved: boolean = false;

  constructor(
    private preBillService: PreBillService,
    public utils: Utils,
    private spinner: NgxSpinnerService,
    private cargoManager: CargoManager,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
    private cargoDetailService: CargoDetailService,
    private companyNamePipe: CompanyNamePipe,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.paginationList.setPageSize(25);
    this.initialTransportCompanyId = this.route.snapshot.queryParams['transportCompanyId'] || null;
    this.listenFilters();
    // La primera consulta la dispara la pastilla al resolver la empresa (onTransportCompanyChange).
  }

  /** null = SaaS sin transporte: se lista sin transportCompanyId (comportamiento previo). */
  public onTransportCompanyChange(transportCompanyId: string): void {
    this.transportCompanyId = transportCompanyId;
    this.transportCompanyResolved = true;
    this.refreshList();
    if (!transportCompanyId || this.route.snapshot.queryParams['transportCompanyId'] === transportCompanyId) return;
    // replaceUrl: cambiar de empresa no agrega pasos al historial del navegador.
    this.router.navigate([], { relativeTo: this.route, queryParams: { transportCompanyId }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  private listenFilters(): void {
    this.subscriptions.push(
      this.companyControl.valueChanges.subscribe(company => {
        if (company === 'clean' || !company) {
          if (this.lastCompanyId !== undefined) {
            this.lastCompanyId = undefined;
            this.refreshList();
          }
        } else {
          const nextCompanyId = company && company.companyId ? company.companyId : undefined;
          if (this.lastCompanyId !== nextCompanyId) {
            this.lastCompanyId = nextCompanyId;
            this.refreshList();
          }
        }
      }),
    );
  }

  public onSelectTab(index: number): void {
    const tab = this.tabs[index];
    if (!tab || tab.state === this.activeState) return;
    this.activeState = tab.state;
    if (this.activeState !== PreBillState.Created) this.validatedControl.setValue('all', { emitEvent: false });
    this.refreshList();
  }

  public refreshList(): void {
    this.resetPagging();
    this.getListPreBill();
  }

  public searchByConsecutive(): void {
    this.refreshList();
  }

  public onScrollDown(): void {
    if (!this.paginationList.getEnablePagingScroll()) return;
    this.paginationList.setPageKey(this.paginationList.getPageKey() + 1);
    this.getListPreBill();
  }

  private getListPreBill(): void {
    if (!this.transportCompanyResolved) return;
    this.listLoading = this.paginationList.getPageKey() === 1;
    this.spinner.show();
    const consecutive = this.consecutiveControl.value
      ? Number(this.consecutiveControl.value)
      : undefined;
    this.preBillService.getPreBills(
      this.activeState as PreBillState,
      this.paginationList.getPageKey(),
      this.paginationList.getPageSize(),
      consecutive,
      this.lastCompanyId,
      this.prebillRequiresValidation && this.validatedControl.value !== 'all' ? this.validatedControl.value : undefined,
      this.transportCompanyId || undefined
    ).subscribe(
      (data: ResponseList<PreBill>) => {
        if (!this.utils.isDefined(data) || !data.results || !data.results.length) {
          this.paginationList.setEnablePagingScroll(false);
          data = data || {} as ResponseList<PreBill>;
          data.results = [];
        }
        if (this.paginationList.getList().length) this.paginationList.setList(this.paginationList.getList().concat(data.results));
        else this.paginationList.setList(data.results);
        this.spinner.hide();
        this.listLoading = false;
      },
      () => {
        this.spinner.hide();
        this.listLoading = false;
        if (!this.paginationList.getList().length) this.resetPagging();
      }
    );
  }

  private resetPagging(): void {
    this.paginationList.setPageKey(1);
    this.paginationList.setList([]);
    this.paginationList.setEnablePagingScroll(true);
    this.expandedIds = {};
    this.loadingCargosIds = {};
    this.loadingPreviewIds = {};
    this.previews = {};
  }

  public getCargosCount(preBill: PreBill): number {
    return preBill && preBill.cargoCount ? preBill.cargoCount : 0;
  }

  public canExpand(preBill: PreBill): boolean {
    return this.getCargosCount(preBill) > 0;
  }

  public hasConfiguredPreview(preBill: PreBill): boolean {
    const preview = this.getPreview(preBill);
    return !!(preview && preview.columns && preview.columns.length);
  }

  public shouldShowMissingPreviewAlert(preBill: PreBill): boolean {
    return !!(preBill && preBill.state !== PreBillState.Deleted && !this.hasConfiguredPreview(preBill));
  }

  public canShowEditAction(): boolean {
    return this.activeState === PreBillState.Created;
  }

  public get prebillRequiresValidation(): boolean {
    return this.authService && this.authService.hasCompanySettingEnabled(SettingId.PREBILL_REQUIRES_VALIDATION);
  }

  public getEditDisabledReason(preBill: PreBill): string {
    if (this.prebillRequiresValidation && preBill && preBill.validated)
      return 'No se puede editar una prefactura validada';
    return '';
  }

  public onEditPreBill(preBill: PreBill): void {
    if (this.getEditDisabledReason(preBill)) return;
    this.openPreBillForm('update', preBill);
  }

  public canShowDownloadAction(preBill: PreBill): boolean {
    if (!preBill || !preBill.state) return false;
    return [PreBillState.Created, PreBillState.Cashed].includes(preBill.state);
  }

  public getCargoOrigin(cargo: Cargo): string {
    const origin: Origin = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.origin');
    return origin ? origin.name : null;
  }

  public getCargoDestination(cargo: Cargo): string {
    const destination: Destination[] = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.destination');
    return destination && destination.length ? destination[destination.length - 1].name : null;
  }

  public getCargoRate(cargo: Cargo): number {
    const rate = this.utils.getNestedValue(cargo, 'shippingCost.rate');
    return rate ? rate : null;
  }

  public openCargoDetail(cargo: Cargo): void {
    this.utils.openCargoNewTab(cargo);
  }

  public isExpanded(preBill: PreBill): boolean {
    if (!preBill || !preBill.id) return false;
    return !!this.expandedIds[preBill.id];
  }

  public isLoadingPreview(preBill: PreBill): boolean {
    if (!preBill || !preBill.id) return false;
    return !!this.loadingPreviewIds[preBill.id];
  }

  public getPreview(preBill: PreBill): PreBillPreview {
    if (!preBill || !preBill.id) return null;
    return this.previews[preBill.id] || null;
  }

  public getColumnLetter(index: number): string {
    let result = '';
    let current = index;
    while (current >= 0) {
      result = String.fromCharCode((current % 26) + 65) + result;
      current = Math.floor(current / 26) - 1;
    }
    return result;
  }

  public getPreviewCellValue(row: PreBillPreviewRow, columnId: string): string {
    if (!row || !row.values || !columnId) return '';
    const value = row.values[columnId];
    if (!this.utils.isDefined(value)) return '';
    return String(value);
  }

  public buildPreviewFileName(preBill: PreBill): string {
    const consecutive = preBill && preBill.consecutive ? preBill.consecutive : '';
    if (!preBill || !preBill.companyId) return '';
    const name = this.companyNamePipe.transform(preBill.companyId);
    if (!name) return '';
    const companyName = String(name).replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (companyName) return `Prefactura_${consecutive}_${companyName}`;
    return `Prefactura_${consecutive}`;
  }

  public hasPreviewRowCargo(row: PreBillPreviewRow): boolean {
    return !!(row && row.cargoId);
  }

  public onDownloadPreview(preBill: PreBill): void {
    if (!preBill || !preBill.id) return;
    this.spinner.show();
    this.preBillService.downloadPreBillPreviewExcel(preBill.id).subscribe(
      (blob: Blob) => {
        this.utils.downloadFile(blob, this.buildPreviewFileName(preBill), 'application/xslx');
      },
      () => {
        this.spinner.hide();
        this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
      }
    );
  }

  public async onPreviewRowClick(row: PreBillPreviewRow): Promise<void> {
    if (!this.hasPreviewRowCargo(row)) return;
    this.spinner.show();
    try {
      const cargo = await this.cargoDetailService.detailCargo(row.cargoId).toPromise();
      this.spinner.hide();
      if (!cargo) {
        this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
        return;
      }
      this.utils.openCargoNewTab(cargo);
    } catch (error) {
      this.spinner.hide();
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
    }
  }

  private hasCargosLoaded(preBill: PreBill): boolean {
    return !!(preBill && Array.isArray(preBill.cargos));
  }

  private async ensurePreBillCargos(preBill: PreBill): Promise<PreBill> {
    if (!preBill || !preBill.id) return preBill;
    if (this.hasCargosLoaded(preBill)) return preBill;

    this.loadingCargosIds[preBill.id] = true;
    this.spinner.show();
    try {
      const detail = await this.preBillService.getPreBill(preBill.id).toPromise();
      preBill['cargos'] = detail && Array.isArray(detail.cargos) ? detail.cargos : [];
      return preBill;
    } catch (error) {
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
      return preBill;
    } finally {
      this.loadingCargosIds[preBill.id] = false;
      this.spinner.hide();
    }
  }

  public async toggleExpand(preBill: PreBill): Promise<void> {
    if (!preBill || !preBill.id || !this.canExpand(preBill)) return;
    const willExpand = !this.expandedIds[preBill.id];
    this.expandedIds[preBill.id] = willExpand;
    if (!willExpand) return;

    try {
      await this.ensureExpandedContent(preBill);
    } catch (error) {
      this.expandedIds[preBill.id] = false;
    }
  }

  private async ensureExpandedContent(preBill: PreBill): Promise<void> {
    if (!preBill || !preBill.id) return;
    const hasPreviewCached = !!this.previews[preBill.id];
    const needsFallbackCargos = hasPreviewCached && !this.hasConfiguredPreview(preBill) && !this.hasCargosLoaded(preBill);
    if (hasPreviewCached && !needsFallbackCargos) return;
    this.spinner.show();
    if (preBill.state !== PreBillState.Deleted) {
      this.loadingPreviewIds[preBill.id] = true;
      try {
        if (!hasPreviewCached) {
          const preview = await this.preBillService.getPreBillPreview(preBill.id).toPromise();
          this.previews[preBill.id] = {
            columns: preview && preview.columns ? preview.columns : [],
            rows: preview && preview.rows ? preview.rows : []
          };
        }
      } catch (error) { } finally {
        this.loadingPreviewIds[preBill.id] = false;
      }
    }
    try {
      if (!this.hasConfiguredPreview(preBill) && !this.hasCargosLoaded(preBill)) {
        const detail = await this.preBillService.getPreBill(preBill.id).toPromise();
        preBill['cargos'] = detail && Array.isArray(detail.cargos) ? detail.cargos : [];
      }
    } catch (error) { }
    this.spinner.hide();
  }

  public trackByPreBill(index: number, preBill: PreBill): string | number {
    return preBill && preBill.id ? preBill.id : preBill && preBill.consecutive ? preBill.consecutive : index;
  }

  public trackByPreviewColumn(index: number, column: PreBillPreviewColumn): string | number {
    return column && column.id ? column.id : index;
  }

  public trackByPreviewRow(index: number, row: PreBillPreviewRow): string | number {
    return row && row.cargoId ? row.cargoId : index;
  }

  public trackByCargo(index: number, cargo: Cargo): string | number {
    return cargo && cargo.id ? cargo.id : cargo && cargo.consecutive ? cargo.consecutive : index;
  }

  public async onViewPreBillHistory(preBill: PreBill): Promise<void> {
    if (!preBill || !preBill.id) return;

    this.spinner.show();
    let history: PreBillFingerprintHistory[] = [];
    try {
      history = await this.preBillService.getPreBillHistory(preBill).toPromise();
    } catch (error) {
      this.spinner.hide();
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
      return;
    }
    this.spinner.hide();
    if (!history.length)
      return this.snackBarService.openSnackBar('No se encontraron cambios registrados para esta prefactura', undefined, 'info');

    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: `Historial de la prefactura #${preBill.consecutive}`,
      descriptionHTML: this.getPreBillHistoryDescription(history),
      hideBtnConfirm: true
    };
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.width = ModalEnum.EXTRA_SMALL_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.autoFocus = false;
    this.dialog.open(DialogComponent, dialogConfig);
  }

  private getPreBillStateLabel(state: string): string {
    const tab = this.tabs.find(item => item.state === state);
    return tab ? tab.stateLabel : state || '-';
  }

  private getPreBillHistoryActionLabel(historyItem: PreBillFingerprintHistory): string {
    const description = this.utils.getNestedValue(historyItem, 'data.description') as string;
    if (description) return description;
    switch (historyItem.fingerprintType) {
      case PreBillFingerprintType.Created:
        return 'Creación de la prefactura';
      case PreBillFingerprintType.CargosUpdated:
        return 'Edición de los servicios de la prefactura';
      case PreBillFingerprintType.CargoRemoved:
        return 'Remoción de un servicio de la prefactura';
      case PreBillFingerprintType.Emptied:
        return 'Eliminación de la prefactura por quedar sin servicios asociados';
      case PreBillFingerprintType.ValidationChanged:
        return 'Cambio de validación de la prefactura';
      case PreBillFingerprintType.Billed:
        return 'Facturación de la prefactura';
      default:
        return 'Cambio en la prefactura';
    }
  }

  private getPreBillHistoryItemDetails(historyItem: PreBillFingerprintHistory): string {
    const fingerprintType = historyItem.fingerprintType;
    const data = historyItem.data || {};
    let detailsHTML = '';

    const addedConsecutives = data.addedConsecutives;
    if (addedConsecutives && addedConsecutives.length) {
      detailsHTML += `<div class="col-12"><strong class="text-bold">Servicios agregados:</strong> ${addedConsecutives.join(', ')}</div>`;
    }

    const removedConsecutives = data.removedConsecutives;
    if (removedConsecutives && removedConsecutives.length) {
      detailsHTML += `<div class="col-12"><strong class="text-bold">Servicios removidos:</strong> ${removedConsecutives.join(', ')}</div>`;
    }

    if (this.utils.isDefined(data.totalCargos)) {
      detailsHTML += `<div class="col-12"><strong class="text-bold">Total de servicios:</strong> ${data.totalCargos}</div>`;
    }

    if (this.utils.isDefined(data.cargoConsecutive)) {
      detailsHTML += `<div class="col-12"><strong class="text-bold">Servicio:</strong> ${data.cargoConsecutive}</div>`;
    }

    if (data.reasonDescription) {
      detailsHTML += `<div class="col-12"><strong class="text-bold">Motivo:</strong> ${data.reasonDescription}</div>`;
    }

    if (fingerprintType === PreBillFingerprintType.ValidationChanged && this.utils.isDefined(data.validated)) {
      detailsHTML += `<div class="col-12"><strong class="text-bold">Estado de validación:</strong> ${data.validated ? 'Validado' : 'No validado'}</div>`;
    }

    if (data.previousState) {
      detailsHTML += `<div class="col-12"><strong class="text-bold">Estado anterior:</strong> ${this.getPreBillStateLabel(data.previousState)}</div>`;
    }

    if (data.newState) {
      detailsHTML += `<div class="col-12"><strong class="text-bold">Estado nuevo:</strong> ${this.getPreBillStateLabel(data.newState)}</div>`;
    }

    return detailsHTML;
  }

  private getPreBillHistoryDescription(history: PreBillFingerprintHistory[]): string {
    let descriptionHTML = '<small class="my-4">A continuación se muestra el historial de cambios de la prefactura, ordenados desde el más antiguo hasta el más reciente.</small>';
    let indexOfChanges = 1;

    history.forEach((historyItem) => {
      const fingerprintDate = this.utils.getNestedValue(historyItem, 'fingerprint.date') as string;
      const responsibleName = this.utils.getNestedValue(historyItem, 'fingerprint.userName') as string;
      const responsibleDocument = this.utils.getNestedValue(historyItem, 'fingerprint.userId') as string;
      const formattedDate = fingerprintDate
        ? DateManager.formatDate(fingerprintDate, 'YYYY-MM-DD HH:mm ZZ', 'DD/MM/YYYY HH:mm a')
        : '';
      const action = this.getPreBillHistoryActionLabel(historyItem);
      const detailsHTML = this.getPreBillHistoryItemDetails(historyItem);

      descriptionHTML += `
        <div class="mt-2">
          <div class="alert alert-info p-2 px-4 mb-2 d-flex justify-content-between flex-wrap">
            <div class="text-bold">${indexOfChanges}° Cambio</div>
            <div>${formattedDate}</div>
          </div>
          <div class="pl-3 mb-2 d-flex flex-column">
            <div class="col-12"><strong class="text-bold">Acción:</strong> ${action}</div>
            ${detailsHTML}
            ${responsibleName ? `<div class="col-12"><strong class="text-bold">Responsable:</strong> ${responsibleName}${responsibleDocument ? ` (${responsibleDocument})` : ''}</div>` : ''}
          </div>
        </div>
      `;
      indexOfChanges++;
    });

    return descriptionHTML + '<div class="mb-5"></div>';
  }

  public onValidatePreBill(preBill: PreBill, validate: boolean): void {
    if (!this.prebillRequiresValidation) return;
    this.spinner.show();
    this.preBillService.validatePreBill(preBill, validate).subscribe(
      () => {
        this.snackBarService.openSnackBar(validate ? 'Prefactura validada correctamente' : 'Validación reversada correctamente', undefined, 'success');
        this.refreshList();
        this.spinner.hide();
      },
      (error) => {
        const errorMessage = this.utils.getNestedValue(error, 'error.message') as string;
        this.snackBarService.openSnackBar(errorMessage || ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
        this.spinner.hide();
      }
    );
  }

  public async onDeletePreBill(preBill: PreBill): Promise<void> {
    if (!preBill || !preBill.id) return;

    const result = await this.cargoManager.openDialogConfirm(`¿Estás seguro que deseas eliminar la prefactura #${preBill.consecutive}?`);
    if (!result || !result.confirm) return;

    this.spinner.show();
    this.preBillService.deletePreBill(preBill).subscribe(
      () => {
        this.snackBarService.openSnackBar('Prefactura eliminada correctamente', undefined, 'success');
        this.refreshList();
        this.spinner.hide();
      },
      (error) => {
        const errorMessage = this.utils.getNestedValue(error, 'error.message') as string;
        this.snackBarService.openSnackBar(errorMessage || ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
        this.spinner.hide();
      }
    );
  }

  public async openPreBillForm(mode: PreBillFormMode, preBill?: PreBill): Promise<void> {
    // Se crea con la empresa de la pastilla: se espera a que resuelva (milisegundos).
    if (mode === 'create' && !this.transportCompanyResolved) return;
    let preBillData = preBill;
    if (mode === 'update' && preBill && !this.hasCargosLoaded(preBill))
      preBillData = await this.ensurePreBillCargos(preBill);
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { mode, preBill: preBillData, transportCompanyId: mode === 'create' ? this.transportCompanyId : null };
    dialogConfig.width = ModalEnum.MEDIUM_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.autoFocus = false;
    this.dialog.open(PreBillFormComponent, dialogConfig).afterClosed().subscribe((result) => {
      if (result && result.state) this.refreshList();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.resetPagging();
  }
}
